import { useContext, useState } from 'react';
import { AdminMatches } from '@platform/client';
import { AdminTableContext } from '../../AdminTable';
import { useQueryClient } from '@tanstack/react-query';
import { CheckBox, DatePicker, Modal, Spinner } from '@platform/components';
import { HTTPValidationError } from '@platform/client/schemas';
import { FileUploadWithButton } from '../../components/FileUploadWithButton';
import { GreenCircleCheckmark } from '../../../svg/GreenCircleCheckmark';

const INTRODUCTION_IMPORT_TEMPLATE =
  'https://movingworldspublic.blob.core.windows.net/public/introduction_import_template.csv';

export const ImportMatchesModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [file, setFile] = useState<File>();
  const [sendEmail, setSendEmail] = useState(false);
  const [scheduledSendDate, setScheduledSendDate] = useState<Date>();
  const batchImportIntroMutation = AdminMatches.useBatchImportMatches({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [...AdminMatches.getGetAdminMatchesQueryKey({ ...adminQueryOptionsV2 })],
        });
      },
    },
  });
  const handleClose = () => {
    setFile(undefined);
    batchImportIntroMutation.reset();
    onClose();
  };
  const isDone = batchImportIntroMutation.isError || batchImportIntroMutation.isSuccess;
  const queryClient = useQueryClient();
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      variant='dialog'
      headerDivision='divider'
      title='Import Introductions'
      ignoreExtraPadding
      primaryCTA={{
        text: isDone ? 'Close' : 'Import Introductions',
        callback:
          isDone ? handleClose : (
            () =>
              file &&
              batchImportIntroMutation.mutate({
                data: { file: file as unknown as string },
                params: {
                  send_email: sendEmail,
                  scheduled_send_date: scheduledSendDate?.toISOString(),
                },
              })
          ),
        isDisabled: () => !file || batchImportIntroMutation.isPending,
      }}
      secondaryCTA={{
        text: 'Cancel',
        callback: handleClose,
      }}
    >
      <div className='flex flex-col justify-center items-center gap-4'>
        {!batchImportIntroMutation.isPending && !isDone && (
          <>
            <p>
              Upload the completed template below. You can download a copy{' '}
              <a className='blue-link' href={INTRODUCTION_IMPORT_TEMPLATE} download>
                here
              </a>
              .
            </p>
            <div className='max-w-48'>
              <FileUploadWithButton
                onFileUpload={(file) => setFile(file)}
                acceptedFileTypes={['.csv']}
              />
            </div>
            {file && <p className='text-xs text-gray-400'>File name: {file.name}</p>}
            {file && (
              <div className='w-full'>
                <CheckBox
                  description='Send email notification'
                  aria-label='Send email notification'
                  additionalClassnames='mb-2'
                  size='sm'
                  onChange={(isChecked) => setSendEmail(isChecked)}
                />
                <div className='min-h-16'>
                  {sendEmail && (
                    <DatePicker
                      label='Scheduled Send Date (leave blank to send immediately)'
                      onChange={(date) => setScheduledSendDate(date as Date)}
                    />
                  )}
                </div>
              </div>
            )}
          </>
        )}
        {batchImportIntroMutation.isPending && (
          <>
            <Spinner />
            <p>Creating introductions from {file?.name}. This may take a few moments.</p>
          </>
        )}
        {batchImportIntroMutation.isSuccess && (
          <>
            <GreenCircleCheckmark size={24} />
            <p>Introductions created successfully</p>
          </>
        )}
        {batchImportIntroMutation.isError && (
          <>
            <p>There was an error importing the file:</p>
            {(batchImportIntroMutation.error as HTTPValidationError)?.detail?.map((err, i) => (
              <p key={i}>{err.msg}</p>
            ))}
            <p>
              Review the import file for missing values. If the issue continues contact the tech
              team!
            </p>
          </>
        )}
      </div>
    </Modal>
  );
};
